// Public, SSR-safe server fns that surface lightweight listing data for
// Schema.org ItemList JSON-LD on /eat, /do and /happening. Loaders call
// these so AI crawlers see the structured top picks in the raw HTML.

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const SectionInput = z.object({
  section: z.enum(["eat", "do"]),
  limit: z.number().int().min(1).max(50).default(30),
});

export type SeoListItem = {
  slug: string;
  name: string;
  description: string;
  image: string | null;
  city: string | null;
  area: string | null;
  category: string | null;
  updatedAt: string | null;
};

export const getSeoSectionItems = createServerFn({ method: "GET" })
  .inputValidator((input) => SectionInput.parse(input))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await supabaseAdmin
      .from("places")
      .select("slug, name, description, image_url, city_slug, area, category, updated_at")
      .eq("published", true)
      .eq("section", data.section)
      .order("sort_order", { ascending: true })
      .limit(data.limit);
    if (error) throw new Error(error.message);
    const items: SeoListItem[] = (rows ?? [])
      .filter((r: any) => r.slug)
      .map((r: any) => ({
        slug: r.slug,
        name: r.name ?? r.slug,
        description: r.description ?? "",
        image: r.image_url ?? null,
        city: r.city_slug ?? null,
        area: r.area ?? null,
        category: r.category ?? null,
        updatedAt: r.updated_at ?? null,
      }));
    return { items };
  });

export const getSeoHappeningItems = createServerFn({ method: "GET" })
  .inputValidator((input) =>
    z.object({ limit: z.number().int().min(1).max(50).default(30) }).parse(input ?? {}),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const cutoff = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();
    const { data: rows, error } = await supabaseAdmin
      .from("imported_events")
      .select("slug, title, short_description, event_image, city, venue, start_date, end_date, updated_at")
      .eq("approved", true)
      .eq("sync_status", "ok")
      .is("archived_at", null)
      .or(`end_date.gte.${cutoff},and(end_date.is.null,start_date.gte.${cutoff})`)
      .order("start_date", { ascending: true, nullsFirst: false })
      .limit(data.limit);
    if (error) throw new Error(error.message);
    const items = (rows ?? [])
      .filter((r: any) => r.slug)
      .map((r: any) => ({
        slug: r.slug as string,
        name: (r.title ?? r.slug) as string,
        description: (r.short_description ?? "") as string,
        image: (r.event_image ?? null) as string | null,
        city: (r.city ?? null) as string | null,
        venue: (r.venue ?? null) as string | null,
        startDate: (r.start_date ?? null) as string | null,
        endDate: (r.end_date ?? null) as string | null,
        updatedAt: (r.updated_at ?? null) as string | null,
      }));
    return { items };
  });
